import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDeckOutDoorCommonArea,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGardenOutDoorCommonArea,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGazeboOutDoorCommonArea,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasLawnOutDoorCommonArea,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPatioOutDoorCommonArea,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPicnicAreaOutDoorCommonArea,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPlaygroundOutDoorCommonArea,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasRooftopDeckOutDoorCommonArea,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTrailOutDoorCommonArea,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWaterFeatureOutDoorCommonArea,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { type AmenityFlagSave, getIsInputFormDisabled } from '../../utils';

import {
  OUTDOOR_AREA_KEYS,
  OUTDOOR_AREA_METADATA,
  type OutdoorAreaKey,
} from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<OutdoorAreaKey, boolean>;

const OutdoorAreasAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const outdoorCommonAreaTypes = listingDetails?.buildingAttributes?.outdoorCommonAreaTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasDeck: outdoorCommonAreaTypes?.hasDeck ?? false,
    hasGarden: outdoorCommonAreaTypes?.hasGarden ?? false,
    hasGazebo: outdoorCommonAreaTypes?.hasGazebo ?? false,
    hasLawn: outdoorCommonAreaTypes?.hasLawn ?? false,
    hasPatio: outdoorCommonAreaTypes?.hasPatio ?? false,
    hasPicnicArea: outdoorCommonAreaTypes?.hasPicnicArea ?? false,
    hasPlayground: outdoorCommonAreaTypes?.hasPlayground ?? false,
    hasRooftopDeck: outdoorCommonAreaTypes?.hasRooftopDeck ?? false,
    hasTrail: outdoorCommonAreaTypes?.hasTrail ?? false,
    hasWaterFeature: outdoorCommonAreaTypes?.hasWaterFeature ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Outdoor common areas updated',
  });

  const isInputFormDisabled = getIsInputFormDisabled(listingDetails, canUpdateListing);

  const { mutateAsync: updateDeck } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDeckOutDoorCommonArea,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasDeckOutDoorCommonArea',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateGarden } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGardenOutDoorCommonArea,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGardenOutDoorCommonArea',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateGazebo } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGazeboOutDoorCommonArea,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasGazeboOutDoorCommonArea',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateLawn } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasLawnOutDoorCommonArea,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasLawnOutDoorCommonArea',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updatePatio } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPatioOutDoorCommonArea,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPatioOutDoorCommonArea',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updatePicnicArea } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPicnicAreaOutDoorCommonArea,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPicnicAreaOutDoorCommonArea',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updatePlayground } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPlaygroundOutDoorCommonArea,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasPlaygroundOutDoorCommonArea',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateRooftopDeck } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasRooftopDeckOutDoorCommonArea,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasRooftopDeckOutDoorCommonArea',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateTrail } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTrailOutDoorCommonArea,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasTrailOutDoorCommonArea',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateWaterFeature } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWaterFeatureOutDoorCommonArea,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWaterFeatureOutDoorCommonArea',
        details: { listingAlias },
      },
    },
  });

  const mutationMap = {
    hasDeck: updateDeck,
    hasGarden: updateGarden,
    hasGazebo: updateGazebo,
    hasLawn: updateLawn,
    hasPatio: updatePatio,
    hasPicnicArea: updatePicnicArea,
    hasPlayground: updatePlayground,
    hasRooftopDeck: updateRooftopDeck,
    hasTrail: updateTrail,
    hasWaterFeature: updateWaterFeature,
  } as unknown as Record<keyof LocalState, AmenityFlagSave>;

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as OutdoorAreaKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = OUTDOOR_AREA_METADATA[fieldName];
      await mutationMap[fieldName]({
        postData: {
          [metadata.apiKey]: checked,
          listingAlias,
        },
      });
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Outdoor common areas</Legend>} disabled={isInputFormDisabled}>
      {OUTDOOR_AREA_KEYS.map((fieldName) => {
        const metadata = OUTDOOR_AREA_METADATA[fieldName];
        return (
          <LabeledControl
            key={`outdoorCommonAreaTypes-${metadata.label}`}
            label={<Label>{metadata.label}</Label>}
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(OutdoorAreasAmenities);
